/**
 * siteVlanApplied -- is each SITE VLAN actually on every gateway and switch of the site?
 *
 * Gateway > Configure > VLANs shows the site VLAN table (the list is the site's, not a box's)
 * with an "Applied" column. This module derives that column, the auto-deploy headline and the
 * plain-language refusal text. It is PURE -- no React, no fetch -- so the rules are unit-tested
 * (siteVlanApplied.test.ts, `npm run test:unit`) and the route only wires data in.
 *
 * OWNER FEEDBACK (2026-09-14) THAT SHAPES THE WORDING
 *   * "Not applied / Partial · GW 0/2 · SW 0/2 -- not sure what it means." The column is now ONE
 *     status word whose number matches the device names under it: "Live on all devices",
 *     "Deploying", "Missing on 2 devices" + "Missing on JH-GW-02, ECS4150-28P", "Can't deploy".
 *     The old contradiction (label Partial, counts 0/2) came from a switch that has the VLAN in
 *     its VLAN database but not on its trunks: it was called Partial yet counted as 0. It is now
 *     named for what it is -- "on the switch, not yet on its uplinks" -- and counted as missing.
 *   * VLAN changes deploy automatically (services/site_vlan_auto_deploy), so the headline speaks
 *     about the deploy ("Deploying to 2 gateways and 2 switches…"), not about a review.
 *
 * SOURCES OF TRUTH (and why each one)
 *
 *   Gateways  the gateway's LIVE networks table (GET /devices/{serial}/gateway/networks, i.e. the
 *             config the controller holds for the box). A VLAN is live on a gateway when a
 *             network with that VLAN id exists AND carries the address the rollout would give it:
 *               HA pair  -> network + physical host (primary .2, secondary .3); .1 is the VIP
 *               single   -> the site VLAN's gateway_ip, else network + 1
 *             Same rule as services/site_vlan_rollout.gateway_networks, so the column and the
 *             rollout plan cannot disagree. Present with another address/subnet = "drift" (the
 *             rollout REFUSES that VLAN on that box: gateway_config.site_vlan_conflicts).
 *             Present with NO address at all (e.g. the `vlan1254` stub an SSID fan-out left on
 *             JH-GW-01/02, 2026-09-14) = "pending": the rollout ADOPTS that network
 *             (gateway_config.site_vlan_adoptions), and once adopted it reads "applied" whatever
 *             its name, because only VLAN id + address are compared.
 *
 *   Switches  the READ-ONLY rollout plan (GET /sites/{id}/vlan-rollout/plan, ~0.4 s): per switch
 *             `vlan_db_existing` (read from its state document) and `create_vlans`. A switch's
 *             trunk tagging is NOT in its state document, so "on its uplinks" is only claimed
 *             with evidence from the last rollout run: the switch applied it WHILE it had trunk
 *             ports to tag, or a Verify read it back. An "applied" run that had NO trunk ports
 *             created the VLANs in the database and tagged nothing -- it is not evidence of uplink
 *             tagging (see switchVlanStatus). A VLAN in the VLAN database without that evidence is
 *             "created" -- complete only when the switch has no trunk ports to tag.
 *
 *   Removals  a DELETED site VLAN stays pending (a tombstone) until a rollout removes it from
 *             every device; summarizeRemoval says which devices still carry it.
 */
import type { GatewayNetworkRow, GatewayNetworkTable, HealthState } from "@/types/domain";
import type {
  AutoDeployState,
  AutoDeployView,
  DhcpSource,
  HaPairView,
  RolloutItem,
  RolloutSwitchPlan,
  RolloutVerification,
  SiteVlanRow,
  VlanRollout,
} from "@/lib/api/siteVlans";
import { formatSwitchPort } from "./switchPorts.ts";

/* ---- IPv4 helpers ------------------------------------------------------------------------- */

/** "10.0.0.1" -> 167772161, or null for anything that is not a dotted quad. */
export function ipv4ToInt(ip: string | null | undefined): number | null {
  const parts = String(ip ?? "").trim().split(".");
  if (parts.length !== 4) return null;
  let out = 0;
  for (const p of parts) {
    if (!/^\d{1,3}$/.test(p)) return null;
    const n = Number(p);
    if (n > 255) return null;
    // Multiply rather than shift: `<<` is signed 32-bit and turns 128.x.x.x negative.
    out = out * 256 + n;
  }
  return out;
}

export function intToIpv4(n: number): string {
  return [24, 16, 8, 0].map((s) => Math.floor(n / 2 ** s) % 256).join(".");
}

/** "10.150.150.0/24" (or a host inside it) -> {network, prefix}; null when unparseable. */
export function parseCidr(cidr: string | null | undefined): { network: number; prefix: number } | null {
  const [ip, len] = String(cidr ?? "").trim().split("/");
  const addr = ipv4ToInt(ip);
  const prefix = Number(len);
  if (addr == null || len === undefined || !/^\d{1,2}$/.test(len) || prefix > 32) return null;
  const size = 2 ** (32 - prefix);
  return { network: Math.floor(addr / size) * size, prefix };
}

/** The address `host` positions into the subnet (network + host), or null. */
export function hostInSubnet(subnet: string | null | undefined, host: number): string | null {
  const net = parseCidr(subnet);
  return net ? intToIpv4(net.network + host) : null;
}

/** Two CIDRs name the same network (host bits and formatting ignored). */
export function sameSubnet(a: string | null | undefined, b: string | null | undefined): boolean {
  const x = parseCidr(a);
  const y = parseCidr(b);
  return !!x && !!y && x.network === y.network && x.prefix === y.prefix;
}

/* ---- gateway side ------------------------------------------------------------------------- */

export type GatewayRole = "primary" | "secondary" | "single";

/** Server defaults (services/gateway_ha.DEFAULTS); the HA pair view carries the live values. */
const DEFAULT_PHYSICAL_HOSTS: Record<string, number> = { primary: 2, secondary: 3 };
const DEFAULT_VIP_HOST = 1;

type VlanAddressing = Pick<SiteVlanRow, "subnet" | "gateway_ip">;

/**
 * The site-level "VLAN interface IP": the address clients use as their gateway. On an HA pair
 * that is the VIP (the server refuses any other gateway_ip), on a single gateway gateway_ip or .1.
 */
export function siteGatewayAddress(vlan: VlanAddressing, haPair: HaPairView | null): string | null {
  if (!vlan.subnet) return null;
  if (haPair) return hostInSubnet(vlan.subnet, haPair.addressing?.vip_host ?? DEFAULT_VIP_HOST);
  return vlan.gateway_ip || hostInSubnet(vlan.subnet, DEFAULT_VIP_HOST);
}

/** The address ONE gateway must hold for this VLAN (mirrors gateway_networks in the rollout). */
export function expectedGatewayAddress(
  vlan: VlanAddressing,
  role: GatewayRole,
  haPair: HaPairView | null,
): string | null {
  if (!vlan.subnet) return null;
  if (role === "single") return vlan.gateway_ip || hostInSubnet(vlan.subnet, DEFAULT_VIP_HOST);
  const hosts = haPair?.addressing?.physical_hosts ?? DEFAULT_PHYSICAL_HOSTS;
  return hostInSubnet(vlan.subnet, hosts[role] ?? DEFAULT_PHYSICAL_HOSTS[role] ?? 2);
}

/** A site gateway's role: from the HA pair when there is one, else "single". */
export function gatewayRole(serial: string, haPair: HaPairView | null): GatewayRole {
  if (haPair?.primary?.serial === serial) return "primary";
  if (haPair?.secondary?.serial === serial) return "secondary";
  return "single";
}

export type GatewayVlanState = "applied" | "untagged" | "pending" | "drift" | "l2_only" | "unknown";

/**
 * A live network row with no IPv4 addressing at all -- what the rollout adopts rather than
 * refuses. Mirrors services/gateway_config._is_unaddressed: no address, no subnet, and an
 * addressing mode that is absent or "none" (dynamic/static are addressing someone chose).
 */
export function isUnaddressed(row: Pick<GatewayNetworkRow, "interface_ip" | "subnet" | "addressing">): boolean {
  return !row.interface_ip && !row.subnet && (row.addressing == null || row.addressing === "none");
}

export interface GatewayVlanStatus {
  state: GatewayVlanState;
  /** The address this gateway should hold, e.g. "10.150.150.2". */
  expected: string | null;
  /** The live network carrying this VLAN id on the gateway, if any. */
  observed: GatewayNetworkRow | null;
}

export function gatewayVlanStatus(
  vlan: Pick<SiteVlanRow, "vlan_id" | "subnet" | "gateway_ip">,
  role: GatewayRole,
  haPair: HaPairView | null,
  table: GatewayNetworkTable | null | undefined,
): GatewayVlanStatus {
  const expected = expectedGatewayAddress(vlan, role, haPair);
  // A VLAN without a subnet is layer 2 only: the rollout gives the gateway no interface.
  if (!vlan.subnet) return { state: "l2_only", expected: null, observed: null };
  if (!table) return { state: "unknown", expected, observed: null };
  const observed = table.networks.find((n) => n.vlan === vlan.vlan_id) ?? null;
  if (!observed) return { state: "pending", expected, observed: null };
  // An unaddressed network on this VLAN is adopted by the next deploy: owed, not a conflict.
  if (isUnaddressed(observed)) return { state: "pending", expected, observed };
  const matches = observed.interface_ip === expected && sameSubnet(observed.subnet, vlan.subnet);
  if (!matches) return { state: "drift", expected, observed };
  // Addressed correctly is NOT the same as working. A gateway network whose ports carry no
  // 802.1Q tag egresses UNTAGGED: the address is up, the column said "Live", and not one
  // tagged frame left the box (Jedi Home, 2026-09-14). `tagged === false` is that state; it is
  // the gateway twin of a switch that has the VLAN in its database but not on its uplinks.
  // `tagged == null` (an older controller that did not report the field, or an interface with
  // no ports) leaves the old behaviour alone rather than inventing a fault.
  if (observed.tagged === false) return { state: "untagged", expected, observed };
  return { state: "applied", expected, observed };
}

/* ---- switch side -------------------------------------------------------------------------- */

export type SwitchVlanState = "tagged" | "created" | "pending" | "unknown";

export interface SwitchVlanStatus {
  state: SwitchVlanState;
  /** True when nothing further is owed on this switch for this VLAN. */
  done: boolean;
}

export function switchVlanStatus(
  vlanId: number,
  plan: RolloutSwitchPlan | null | undefined,
  rollout: VlanRollout | null | undefined,
): SwitchVlanStatus {
  if (!plan) return { state: "unknown", done: false };
  // No state document => the plan could not read the VLAN database at all.
  if (plan.refusals.some((r) => r.code === "switch_no_state")) return { state: "unknown", done: false };

  const inDb = plan.vlan_db_existing.includes(vlanId);
  const pendingCreate = plan.create_vlans.some((c) => c.vlan === vlanId);
  const coveredByRun = !!rollout && rollout.vlan_ids.includes(vlanId);

  // Strongest evidence first: a Verify read of this switch after a run that included the VLAN.
  const verified = coveredByRun
    ? rollout!.verification?.devices.find((d) => d.serial === plan.serial)
    : undefined;
  if (verified && (verified.status === "verified" || verified.status === "not_observed")) {
    if (verified.missing_vlans?.includes(vlanId)) return { state: "pending", done: false };
    const untagged = (verified.ports ?? []).some((p) => p.missing_tagged.includes(vlanId));
    if (untagged) return { state: "created", done: false };
    if (inDb) return { state: "tagged", done: true };
  }

  // A completed run that applied this switch with this VLAN, and the VLAN is still in its DB.
  const ran = coveredByRun
    ? rollout!.devices.find((d) => d.serial === plan.serial && d.status === "applied")
    : undefined;
  // "applied" only proves the CLI session ran with no rejected line -- it tagged the UPLINKS
  // only if the run actually had trunk ports. A run that applied with NO trunk ports created the
  // VLAN in the database and tagged nothing, so DB-presence after it is NOT evidence of uplink
  // tagging. This is the ECS4150-28P / VLAN 1254 case (2026-09-14): the run adopted 1254 on the
  // gateways and created it in the switch database, but the switch's uplink LLDP had not resolved
  // (right after the VRRP cutover dropped the gateway link), so ports 1/1-1/2 were not classed as
  // trunks and never carried 1254 -- yet the column read "Live". Require the run to have tagged
  // at least one trunk port before claiming "on its uplinks"; otherwise fall through to
  // "created" (owed while the switch still has uplinks to tag). A Verify read-back (above) is the
  // stronger, per-port evidence and overrides this whenever it exists.
  if (ran && inDb && !pendingCreate && (ran.trunk_ports?.length ?? 0) > 0) {
    return { state: "tagged", done: true };
  }

  if (pendingCreate) return { state: "pending", done: false };
  if (inDb) return { state: "created", done: plan.trunk_ports.length === 0 };
  return { state: "unknown", done: false };
}

/* ---- the automatic read-back (OP#1822) ---------------------------------------------------- */

/**
 * One line per switch the read-back found missing a site VLAN, e.g.
 * "Missing on ECS2100-10P-TIPC: VLAN 1000 not in its VLAN database · port 1/2 not tagged 1000".
 * Empty when nothing is missing (or no read-back ran). A switch that could not be READ is not
 * here: the controller lists only switches it read and found lacking (verification.missing).
 */
export function verificationMissingLines(verification: RolloutVerification | null | undefined): string[] {
  return (verification?.missing ?? []).map((m) => {
    const parts: string[] = [];
    if (m.missing_vlans.length) {
      parts.push(`VLAN ${m.missing_vlans.join(", ")} not in its VLAN database`);
    }
    const ports = Object.entries(m.missing_tagged).sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }));
    for (const [port, vlans] of ports) {
      parts.push(`port ${formatSwitchPort(port, "cli")} not tagged ${vlans.join(", ")}`);
    }
    return `Missing on ${m.name}: ${parts.join(" · ")}`;
  });
}

/** The read-back pill: what it says and how loud. */
export function verificationWord(
  verification: RolloutVerification | null | undefined,
): { tone: HealthState; label: string } | null {
  if (!verification) return null;
  if (verification.status === "error") return { tone: "critical", label: "Read-back failed" };
  if ((verification.missing?.length ?? 0) > 0) {
    return { tone: "critical", label: `Missing on ${verification.missing!.length} switch${verification.missing!.length === 1 ? "" : "es"}` };
  }
  if (verification.status === "verified") return { tone: "healthy", label: "Verified on devices" };
  // Every device the run configured read back fine, but switches were skipped while offline
  // (owner decision 2026-10-13): honest, not green -- the site is not complete yet.
  if (verification.status === "verified_except_skipped") {
    const n = verification.skipped_offline?.length ?? 0;
    // Owner decision 2026-10-14: switches HELD for their re-plan are also left out.
    const h = verification.held_pending_replan?.length ?? 0;
    const parts = [
      n ? `${n} switch${n === 1 ? "" : "es"} skipped (offline)` : "",
      h ? `${h} switch${h === 1 ? "" : "es"} held for re-plan` : "",
    ].filter(Boolean);
    return { tone: "warn", label: `Verified · ${parts.join(" · ") || "some switches left out"}` };
  }
  return { tone: "warn", label: "Not fully verified" };
}

/* ---- per-device words --------------------------------------------------------------------- */

export interface DeviceApplied {
  serial: string;
  name: string;
  kind: "gateway" | "switch";
  /** gateway role, or null for a switch */
  role: GatewayRole | null;
  state: GatewayVlanState | SwitchVlanState;
  done: boolean;
  /** Short operator text for the expanded row, e.g. "10.150.150.2 configured". */
  detail: string;
}

/** A switch that has the VLAN in its database but not (provably) on its trunk ports. */
export function isOnSwitchNotUplinks(d: DeviceApplied): boolean {
  return d.kind === "switch" && d.state === "created" && !d.done;
}

/** A gateway that has the VLAN addressed but NOT tagged on its LAN ports (so it carries nothing). */
export function isGatewayUntagged(d: DeviceApplied): boolean {
  return d.kind === "gateway" && d.state === "untagged";
}

/** The pill for one device in the expanded row. Status ramp = operational state only. */
export function deviceStatusWord(d: DeviceApplied): { tone: HealthState; label: string } {
  if (d.done && d.state !== "l2_only") return { tone: "healthy", label: "Live" };
  switch (d.state) {
    case "created":
      return { tone: "warn", label: "On switch, not uplinks" };
    case "untagged":
      return { tone: "critical", label: "Configured but untagged" };
    case "pending":
      return { tone: "warn", label: "Missing" };
    case "drift":
      return { tone: "critical", label: "Different addressing" };
    case "l2_only":
      return { tone: "unknown", label: "Layer 2 only" };
    default:
      return { tone: "unknown", label: "Not reported" };
  }
}

const GATEWAY_DETAIL: Record<GatewayVlanState, (s: GatewayVlanStatus) => string> = {
  applied: (s) => `${s.expected} configured`,
  untagged: (s) =>
    `${s.expected} configured but NOT tagged on ${s.observed?.ports?.join(", ") || "its LAN ports"} — the VLAN leaves the gateway untagged and carries nothing; the next deploy fixes the tagging`,
  pending: (s) =>
    s.observed
      ? `has this VLAN as ${s.observed.name ?? "?"} without an address — the deploy adopts it as ${s.expected ?? "?"}`
      : `not configured yet (will be ${s.expected ?? "?"})`,
  drift: (s) =>
    `has this VLAN as ${s.observed?.name ?? "?"} ${s.observed?.interface_ip ?? "?"} (${s.observed?.subnet ?? "?"}), expected ${s.expected ?? "?"} — Meridian won't re-address it`,
  l2_only: () => "layer 2 only — no gateway interface",
  unknown: () => "its configuration hasn't loaded",
};

const SWITCH_DETAIL: Record<SwitchVlanState, (done: boolean) => string> = {
  tagged: () => "in the VLAN database and on its uplinks (trunk ports)",
  created: (done) =>
    done
      ? "in the VLAN database (no uplinks to carry it)"
      : "in the VLAN database, not yet on its uplinks (trunk ports)",
  pending: () => "not in the VLAN database yet",
  unknown: () => "hasn't reported its VLANs",
};

/* ---- one row's summary -------------------------------------------------------------------- */

export type AppliedKind = "live" | "deploying" | "missing" | "cant_deploy" | "checking" | "no_devices";

export interface RowApplied {
  kind: AppliedKind;
  tone: HealthState | "info";
  /** The status word, e.g. "Missing on 2 devices". Its number always equals `owed.length`. */
  label: string;
  /** The line under it naming devices, e.g. "Missing on JH-GW-02 · On ECS2100, not yet on its uplinks". */
  detail: string | null;
  /** Devices that still owe this VLAN something (missing, on-switch-not-uplinks, conflict). */
  owed: DeviceApplied[];
  /** Devices that did not report (not counted as missing, named separately). */
  unreported: DeviceApplied[];
  gateways: { done: number; total: number };
  switches: { done: number; total: number };
  devices: DeviceApplied[];
}

export interface SummarizeInput {
  vlan: Pick<SiteVlanRow, "vlan_id" | "subnet" | "gateway_ip">;
  haPair: HaPairView | null;
  gateways: { serial: string; name: string; table: GatewayNetworkTable | null | undefined }[];
  /** Plan switches; null while the plan is loading or failed (switches then read "unknown"). */
  switches: { serial: string; name: string }[];
  switchPlans: RolloutSwitchPlan[] | null;
  rollout: VlanRollout | null | undefined;
  /** Auto-deploy state of the site (null = not loaded). */
  deploy?: Pick<AutoDeployView, "state" | "enabled"> | null;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "Missing on A, B · On SW, not yet on its uplinks · C not reporting". */
function owedDetail(prefix: string, owed: DeviceApplied[], unreported: DeviceApplied[]): string | null {
  const parts: string[] = [];
  const plain = owed.filter(
    (d) => !isOnSwitchNotUplinks(d) && !isGatewayUntagged(d) && d.state !== "drift",
  );
  const partial = owed.filter(isOnSwitchNotUplinks);
  const untagged = owed.filter(isGatewayUntagged);
  const drift = owed.filter((d) => d.state === "drift");
  if (plain.length) parts.push(`${prefix} ${plain.map((d) => d.name).join(", ")}`);
  for (const d of partial) parts.push(`On ${d.name}, not yet on its uplinks`);
  // Named for what it is: "Missing on JH-GW-01" would be a lie -- the network is there, it
  // just is not tagged, so nothing rides it.
  for (const d of untagged) parts.push(`On ${d.name} but untagged — carries nothing`);
  for (const d of drift) parts.push(`${d.name} has it with different addressing`);
  if (unreported.length) parts.push(`${unreported.map((d) => d.name).join(", ")} not reporting`);
  return parts.length ? parts.join(" · ") : null;
}

/** Everything the Applied cell and its expanded device list need for one site VLAN. */
export function summarizeVlanApplied(input: SummarizeInput): RowApplied {
  const { vlan, haPair, rollout, deploy } = input;
  const devices: DeviceApplied[] = [];

  for (const gw of input.gateways) {
    const role = gatewayRole(gw.serial, haPair);
    const s = gatewayVlanStatus(vlan, role, haPair, gw.table);
    devices.push({
      serial: gw.serial,
      name: gw.name,
      kind: "gateway",
      role,
      state: s.state,
      done: s.state === "applied" || s.state === "l2_only",
      detail: GATEWAY_DETAIL[s.state](s),
    });
  }
  for (const sw of input.switches) {
    const plan = input.switchPlans?.find((p) => p.serial === sw.serial);
    const s = switchVlanStatus(vlan.vlan_id, plan, rollout);
    devices.push({
      serial: sw.serial,
      name: sw.name,
      kind: "switch",
      role: null,
      state: s.state,
      done: s.done,
      detail: SWITCH_DETAIL[s.state](s.done),
    });
  }

  // L2-only VLANs owe the gateways nothing, so they do not count toward the gateway total.
  const gwRows = devices.filter((d) => d.kind === "gateway" && d.state !== "l2_only");
  const swRows = devices.filter((d) => d.kind === "switch");
  const counted = [...gwRows, ...swRows];
  const gateways = { done: gwRows.filter((d) => d.done).length, total: gwRows.length };
  const switches = { done: swRows.filter((d) => d.done).length, total: swRows.length };
  const owed = counted.filter((d) => !d.done && d.state !== "unknown");
  const unreported = counted.filter((d) => !d.done && d.state === "unknown");

  const base = { owed, unreported, gateways, switches, devices };
  const deploying =
    (rollout?.status === "running" && rollout.vlan_ids.includes(vlan.vlan_id)) ||
    deploy?.state === "running" ||
    deploy?.state === "queued";
  const lastFailed = deploy?.state === "failed" || deploy?.state === "refused";

  if (counted.length === 0) {
    return { ...base, kind: "no_devices", tone: "unknown", label: "No devices", detail: null };
  }
  if (owed.length === 0 && unreported.length === 0) {
    return { ...base, kind: "live", tone: "healthy", label: "Live on all devices", detail: null };
  }
  if (owed.some((d) => d.state === "drift")) {
    return {
      ...base,
      kind: "cant_deploy",
      tone: "critical",
      label: "Can't deploy",
      detail: owedDetail("Missing on", owed, unreported),
    };
  }
  if (owed.length === 0) {
    return {
      ...base,
      kind: "checking",
      tone: "unknown",
      label: "Checking",
      detail: owedDetail("Missing on", owed, unreported),
    };
  }
  if (deploying) {
    return {
      ...base,
      kind: "deploying",
      tone: "info",
      label: "Deploying",
      detail: owedDetail("Adding to", owed, unreported),
    };
  }
  if (lastFailed) {
    return {
      ...base,
      kind: "cant_deploy",
      tone: "critical",
      label: "Can't deploy",
      detail: owedDetail("Missing on", owed, unreported),
    };
  }
  return {
    ...base,
    kind: "missing",
    tone: "warn",
    label: `Missing on ${plural(owed.length, "device", "devices")}`,
    detail: owedDetail("Missing on", owed, unreported),
  };
}

/* ---- deleted site VLANs still on devices -------------------------------------------------- */

export interface RemovalSummary {
  tone: HealthState | "info";
  label: string;
  detail: string;
  /** Device names that still carry the VLAN. */
  stillOn: string[];
}

/**
 * A deleted site VLAN (pending removal): which devices still carry it, in the page's words.
 * Gateways: any live network with that VLAN id. Switches: the VLAN database from the plan.
 */
export function summarizeRemoval(input: {
  vlanId: number;
  gateways: { serial: string; name: string; table: GatewayNetworkTable | null | undefined }[];
  switches: { serial: string; name: string }[];
  switchPlans: RolloutSwitchPlan[] | null;
  deploy: Pick<AutoDeployView, "state"> | null | undefined;
}): RemovalSummary {
  const stillOn: string[] = [];
  for (const gw of input.gateways) {
    if (gw.table?.networks.some((n) => n.vlan === input.vlanId)) stillOn.push(gw.name);
  }
  for (const sw of input.switches) {
    const plan = input.switchPlans?.find((p) => p.serial === sw.serial);
    if (plan?.vlan_db_existing.includes(input.vlanId)) stillOn.push(sw.name);
  }
  const state = input.deploy?.state;
  if (stillOn.length === 0) {
    return { tone: "healthy", label: "Removed", detail: "No device reports it any more", stillOn };
  }
  if (state === "failed" || state === "refused") {
    return { tone: "critical", label: "Can't remove", detail: `Still on ${stillOn.join(", ")}`, stillOn };
  }
  return {
    tone: state === "running" || state === "queued" ? "info" : "warn",
    label: "Removing",
    detail: `Removing from ${stillOn.join(", ")}`,
    stillOn,
  };
}

/* ---- auto-deploy headline + refusals in plain words --------------------------------------- */

/**
 * One refusal as a sentence an operator acts on. Stable codes from services/site_vlan_rollout;
 * anything unmapped falls back to the server's own message (already operator text).
 */
export function plainRefusal(item: RolloutItem, names: Record<string, string>): string {
  const who = item.serial ? (names[item.serial] ?? item.serial) : "A device";
  switch (item.code) {
    case "switch_relay_credential_missing":
      return `Can't reach ${who}: the gateway login for relaying isn't stored yet`;
    case "switch_relay_unavailable":
      return `Can't reach ${who}: no path to it through a site gateway`;
    case "switch_unreachable":
    case "gateway_offline":
      return `${who} is offline`;
    case "gateway_unreachable":
      return `Can't read ${who}'s configuration right now`;
    case "switch_no_state":
      return `${who} hasn't reported its ports and VLANs yet`;
    case "switch_no_mgmt_ip":
      return `${who} has no management address to connect to`;
    case "switch_cli_unavailable":
      return "Switch changes are turned off on this controller";
    case "no_devices":
      return "This site has no gateway or switch to deploy to";
    case "multiple_gateways_no_ha_pair":
      return "This site has two gateways but no HA pair: choose the primary and secondary below";
    case "removed_vlan_in_use":
    case "removed_vlan_usage_unknown":
      // Already a plain sentence naming the users ("... used by SSID 'Guest'. Remove that use,
      // or choose Keep on devices.").
      return item.message;
    case "vlan_id_clash":
    case "vlan_name_clash":
    case "vlan_adopt_wan_ports":
    case "site_vlan_removal_foreign":
    case "gateway_not_editable":
      return `${who}: ${item.message}`;
    default:
      return item.serial ? `${who}: ${item.message}` : item.message;
  }
}

/** One line under a failed/refused deploy; `keepVlan` offers "Keep on devices" for that VLAN. */
export interface DeployReason {
  text: string;
  keepVlan: number | null;
}

export interface DeployHeadline {
  tone: HealthState | "info";
  title: string;
  body: string | null;
  /** Plain refusal / failure lines to list under the body. */
  reasons: DeployReason[];
  /** Offer "Retry now" (the controller never retries on its own). */
  canRetry: boolean;
}

/** "3 min ago" style age of an ISO timestamp relative to `now` (ms). PURE. */
export function timeAgo(iso: string | null | undefined, now: number): string {
  if (!iso) return "";
  // Server timestamps are naive UTC ("2026-09-14T10:00:00"): read them as UTC, not local time.
  const t = Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(t)) return "";
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 45) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return new Date(t).toISOString().slice(0, 10);
}

/** "2 gateways and 2 switches" (drops an empty side). */
export function deviceCountPhrase(gateways: number, switches: number): string {
  const parts: string[] = [];
  if (gateways) parts.push(plural(gateways, "gateway", "gateways"));
  if (switches) parts.push(plural(switches, "switch", "switches"));
  return parts.join(" and ") || "no devices";
}

/**
 * The strip above the table: what auto-deploy is doing, in one headline. null = nothing to say
 * (idle with no history). Never asks the operator to review before a deploy.
 */
export function deployHeadline(
  view: AutoDeployView | null | undefined,
  counts: { gateways: number; switches: number },
  now: number,
): DeployHeadline | null {
  if (!view) return null;
  const run = view.last_run;
  const names: Record<string, string> = {};
  for (const d of run?.devices ?? []) names[d.serial] = d.name;
  const target = deviceCountPhrase(counts.gateways, counts.switches);

  if (!view.enabled) {
    return {
      tone: "unknown",
      title: "Auto-deploy is off",
      body: "Saved VLAN changes wait until auto-deploy is turned back on, or until you review and apply them.",
      reasons: [],
      canRetry: false,
    };
  }
  if (view.state === "running") {
    const total = run?.devices.length ?? 0;
    const index = run ? run.devices.findIndex((d) => d.status === "running") : -1;
    const step = index >= 0 && run ? `Step ${index + 1} of ${total}: ${run.devices[index]!.name}` : null;
    const follow = view.follow_up_queued ? " Your latest change goes out right after." : "";
    return {
      tone: "info",
      title: `Deploying to ${target}…`,
      body: `${step ?? "Switches first, then gateways (secondary before primary)"}.${follow}`,
      reasons: [],
      canRetry: false,
    };
  }
  if (view.state === "queued") {
    const wait = view.seconds_until_due ?? 0;
    return {
      tone: "info",
      title: `Deploying to ${target}…`,
      body:
        wait > 0
          ? `Starts in ${wait} s. Another change restarts the ${view.quiet_seconds} s wait, so edits go out together.`
          : "Starting now.",
      reasons: [],
      canRetry: false,
    };
  }
  if (view.state === "refused" && run) {
    return {
      tone: "critical",
      title: "Couldn't deploy VLAN changes",
      body: "Nothing was changed on any device. Fix what's below, then retry.",
      reasons: (run.refusals ?? []).map((r) => ({
        text: plainRefusal(r, names),
        // A deleted VLAN in use again: the way out is keeping it on the devices.
        keepVlan:
          (r.code === "removed_vlan_in_use" || r.code === "removed_vlan_usage_unknown") && r.vlan != null
            ? r.vlan
            : null,
      })),
      canRetry: true,
    };
  }
  if (view.state === "failed" && run) {
    const failed = run.devices.find((d) => d.status === "failed");
    return {
      tone: "critical",
      title: failed ? `The last deploy stopped at ${failed.name}` : "The last deploy failed",
      body: run.error,
      reasons: failed?.error ? [{ text: failed.error, keepVlan: null }] : [],
      canRetry: true,
    };
  }
  if (view.state === "succeeded" && run) {
    const by = run.trigger === "auto" ? "automatically" : `by ${run.started_by ?? "an operator"}`;
    return {
      tone: "healthy",
      title: `Deployed ${by} ${timeAgo(run.finished_at, now)}`.trim(),
      body: null,
      reasons: [],
      canRetry: false,
    };
  }
  return null;
}

/* ---- context rows from the gateways' live config ------------------------------------------ */

export interface GatewayLiveNetwork {
  name: string;
  subnet: string | null;
  vlan: number | null;
  dhcp: GatewayNetworkRow["dhcp"];
  /** Per gateway: which box holds which address on this network. */
  addresses: { serial: string; gateway: string; ip: string | null }[];
}

/**
 * Networks the gateways carry that the site VLAN table does NOT describe, grouped across
 * gateways by (name, VLAN, subnet): the untagged LAN (shown read-only, labelled untagged) and
 * any tagged VLAN that exists on a box but not in the site list. `excludeVlanIds` hides VLANs
 * the page already shows as "Removing" rows.
 */
export function gatewayOnlyNetworks(
  gateways: { serial: string; name: string; table: GatewayNetworkTable | null | undefined }[],
  siteVlanIds: number[],
  excludeVlanIds: number[] = [],
): { untagged: GatewayLiveNetwork[]; unmanaged: GatewayLiveNetwork[] } {
  const groups = new Map<string, GatewayLiveNetwork>();
  for (const gw of gateways) {
    for (const n of gw.table?.networks ?? []) {
      if (n.vlan != null && (siteVlanIds.includes(n.vlan) || excludeVlanIds.includes(n.vlan))) continue;
      const net = parseCidr(n.subnet);
      const key = `${n.name ?? ""}|${n.vlan ?? "untagged"}|${net ? `${net.network}/${net.prefix}` : n.subnet ?? ""}`;
      const group =
        groups.get(key) ??
        ({ name: n.name ?? "—", subnet: n.subnet, vlan: n.vlan, dhcp: n.dhcp, addresses: [] } as GatewayLiveNetwork);
      group.addresses.push({ serial: gw.serial, gateway: gw.name, ip: n.interface_ip });
      groups.set(key, group);
    }
  }
  const all = Array.from(groups.values());
  return {
    untagged: all.filter((g) => g.vlan == null),
    unmanaged: all.filter((g) => g.vlan != null).sort((a, b) => (a.vlan ?? 0) - (b.vlan ?? 0)),
  };
}

/* ---- live WAN ------------------------------------------------------------------------------- */

/**
 * The locked WAN card's address line from the gateway's state document:
 * "10.111.131.184/25 · DHCP", or null when the gateway has not reported one.
 */
export function wanAddressLine(row: Pick<GatewayNetworkRow, "addressing" | "live">): string | null {
  const live = row.live;
  if (!live?.reported || !live.primary) return null;
  const how = row.addressing === "dynamic" ? "DHCP" : row.addressing === "static" ? "static" : null;
  const extra = live.addresses.length > 1 ? ` (+${live.addresses.length - 1})` : "";
  return `${live.primary}${extra}${how ? ` · ${how}` : ""}`;
}

/* ---- DHCP summary line -------------------------------------------------------------------- */

/**
 * The second line under Subnet. What the rollout will serve, in operator terms:
 * HA pair -> split scope (.10-.129 primary, .130-.249 secondary); single -> gateway DHCP.
 */
export function dhcpSummary(
  vlan: Pick<SiteVlanRow, "subnet" | "dhcp_source" | "dhcp_lease_time">,
  haPair: HaPairView | null,
): string {
  const source: DhcpSource = vlan.dhcp_source ?? "site";
  if (!vlan.subnet) return "Layer 2 only";
  if (source === "none") return "No DHCP";
  if (source === "infoblox") return "External DHCP (no gateway scope)";
  const lease = vlan.dhcp_lease_time ? ` · ${vlan.dhcp_lease_time}` : "";
  const ranges = haPair?.addressing?.dhcp_ranges;
  const p = ranges?.primary;
  const s = ranges?.secondary;
  if (haPair && p && s && p.length === 2 && s.length === 2) {
    return `DHCP split .${p[0]}–.${p[1]} primary · .${s[0]}–.${s[1]} secondary${lease}`;
  }
  return `DHCP on the gateway${lease}`;
}

/** DHCP line for a live gateway network row: "DHCP 100 leases from .10 · 6h". */
export function liveDhcpLine(dhcp: GatewayNetworkRow["dhcp"]): string {
  if (!dhcp) return "No DHCP";
  const last = dhcp.lease_first != null && dhcp.lease_count != null ? dhcp.lease_first + dhcp.lease_count - 1 : null;
  const range = dhcp.lease_first != null && last != null && last <= 254 ? `.${dhcp.lease_first}–.${last}` : `${dhcp.lease_count ?? "?"} leases from .${dhcp.lease_first ?? "?"}`;
  return `DHCP ${range}${dhcp.lease_time ? ` · ${dhcp.lease_time}` : ""}`;
}

/** Re-exported for callers that type a state without importing the API module. */
export type { AutoDeployState };
